
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { Tables } from "@/integrations/supabase/types"
import { toast } from "sonner"
import { useAuth } from "./useAuth"

type UserSetting = Tables<"user_settings">

export const useUserSettings = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["user-settings", profile?.id],
    queryFn: async () => {
      if (!profile?.id) throw new Error("Usuário não autenticado")
      
      const { data, error } = await supabase
        .from("user_settings")
        .select("*")
        .eq("user_id", profile.id)

      if (error) throw error
      return data as UserSetting[]
    },
    enabled: !!profile?.id
  })
}

export const useUpdateUserSetting = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async ({ key, value }: { key: string, value: any }) => {
      if (!profile?.id) throw new Error("Usuário não autenticado")

      const { data, error } = await supabase
        .from("user_settings")
        .upsert({
          user_id: profile.id,
          setting_key: key,
          setting_value: value
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-settings", profile?.id] })
      toast.success("Configuração atualizada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar configuração:", error)
      toast.error("Erro ao atualizar configuração")
    }
  })
}

export const useUpdateProfile = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (updates: Partial<Tables<"profiles">>) => {
      if (!profile?.id) throw new Error("Usuário não autenticado")

      const { data, error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", profile.id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] })
      queryClient.invalidateQueries({ queryKey: ["profile"] })
      toast.success("Perfil atualizado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar perfil:", error)
      toast.error("Erro ao atualizar perfil")
    }
  })
}
